import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { extractErrorMessage, formatCurrency } from '../../helpers';
import PageLayout from '../../components/PageLayout';
import { fetchAffiliateCustomer, loadCustomers } from '../../services/BackendService';
import _t from 'counterpart';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import { CCard, CCardBody, CCardHeader, CCardTitle, CCol, CRow } from '@coreui/react';
import Detail from '../../components/micro/Detail';
import CustomersTable from './CustomersTable';
import { customersQueryOptions } from './customersQueryOptions';
import { ISorter } from '../../components/PaginationTable';
import toast from 'react-hot-toast';

const AffiliatePage = () => {
  const params = useParams();
  const { id } = params as any;
  const [search, setSearch] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>('createdAt|DESC');
  const [page, setPage] = useState<number>(1);

  const customersItemsPerPageFromLocalStorage = Number(localStorage.getItem('items_per_page_affiliates_table') ?? 10);
  const [limit, setLimit] = useState<number>(customersItemsPerPageFromLocalStorage);

  const offset = limit * page - limit;

  const history = useHistory();

  const showError = (errorMessage: string) => {
    const error = extractErrorMessage(errorMessage);
    toast.error(error);
  };

  const queryClient = useQueryClient();

  const loadAffiliateQuery = useQuery(['affiliate-customer', id], () => fetchAffiliateCustomer(id), {
    onError: (e: any) => {
      showError(e);
    },
  });

  const onLimitChanged = async (limit: number) => {
    setLimit(limit);
    localStorage.setItem('items_per_page_customers_table', limit.toString());
    await queryClient.invalidateQueries('customers');
    loadAffiliateQuery.refetch();
  };

  const referralsQuery = useQuery(
    ['customers', search, id, offset, orderBy],
    () => loadCustomers(search, limit, offset, id, orderBy, null),
    customersQueryOptions
  );

  const onPageChanged = (value: number) => {
    setPage(value);
  };

  const onSorterChanged = ({ column, asc }: ISorter) => {
    const sortBy = `${column}|${asc ? 'ASC' : 'DESC'}`;
    setOrderBy(sortBy);
    setPage(1);
  };

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  if (loadAffiliateQuery.isLoading || loadAffiliateQuery.isIdle) {
    return <Loading />;
  }

  if (loadAffiliateQuery.isError) {
    return <Error onRetry={loadAffiliateQuery.refetch} />;
  }

  const onRowClicked = (user: any) => {
    history.push(`/customers/${user.id}`);
  };

  const [column, isAsc] = orderBy!.split('|');
  const asc = isAsc === 'ASC';

  const count = referralsQuery.data?.count ?? limit;
  const pages = Math.ceil(count / Number(limit));

  const {
    name,
    affiliateBalance,
    totalReferrals,
    totalVolumeTraded,
    depositTotal,
    withdrawalTotal = 0,
    netDeposit = 0,
  } = loadAffiliateQuery.data;
  return (
    <PageLayout title={name === ' ' ? _t('global.user-has-no-name') : name} linkTitleTo={`/customers/${id}`}>
      <CRow>
        <CCol md={6}>
          <CCard>
            <CCardHeader>
              <CCardTitle>{_t('global.info')}</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <dl>
                <Detail title={_t('referrals.total-referrals')}>{totalReferrals}</Detail>
                <Detail title={_t('referrals.total-deposit')}>{formatCurrency(depositTotal, 'USD')}</Detail>
                <Detail title={_t('affiliates.withdrawal')}>{formatCurrency(withdrawalTotal, 'USD')}</Detail>
                <Detail title={_t('affiliates.net-deposit')}>{formatCurrency(netDeposit, 'USD')}</Detail>
                <Detail title={_t('affiliates.total-trading-volume')}>{totalVolumeTraded}</Detail>
                <Detail title={_t('affiliates.affiliate-balance')}>{formatCurrency(affiliateBalance, 'USD')}</Detail>
              </dl>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol md={12}>
          <CCard>
            <CCardHeader>
              <CCardTitle>{_t('referrals.title')}</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <CustomersTable
                data={referralsQuery.data?.customers || []}
                onPageChanged={onPageChanged}
                onSorterChanged={onSorterChanged}
                sorter={{ column, asc }}
                isLoading={referralsQuery.isLoading}
                pages={pages}
                activePage={page}
                onRowClicked={onRowClicked}
                onSearchValueChanged={onSearchChange}
                showFilter
                filterValue={search}
                itemsPerPage={limit}
                onLimitChanged={onLimitChanged}
                showCheckboxes={false}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </PageLayout>
  );
};

export default React.memo(AffiliatePage);
